import React, { useState, useEffect, useRef } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { PlayFab } from './services/PlayFab'; // Assuming PlayFab service exists and is correctly typed
// REMOVED: import Card from './components/Card';
// REMOVED: import HandTimelineGrid from './components/HandTimelineGrid';
// REMOVED: import './components/HandTimelineGrid.css';
import { Street, Position, Player, Action as BaseAction, ActionType } from './types'; // Import Action as BaseAction

// Define an interface for the situation structure based on your schema
interface SituationOption {
  action: string;
  is_correct: boolean;
}

// Define structure for actions within streets
interface StreetAction {
    player: string;
    action: string;
}

interface Situation {
  id: string;
  type: string;
  difficulty: string;
  theme: string;
  positions?: { hero: string; villain: string }; // Optional for now
  stacks?: { effective: number }; // Optional for now
  streets: {
    preflop: {
      hero_hand: string[];
      actions: StreetAction[];
    };
    flop?: { // Optional street
      board: string[];
      actions: StreetAction[];
    };
    turn?: { // Optional street
      card: string;
      actions: StreetAction[];
    };
    river?: { // Optional street
      card: string;
      actions: StreetAction[];
    };
  };
  question: string;
  options: SituationOption[];
  explanation: string;
  tags?: string[];
  generationTimestamp?: string; // ADDED: Optional timestamp field
}

// --- REVISED Player structure (Aligns with ./types.ts Player) --- 
// No need for DisplayPlayer if it's identical to Player

// Helper from BuddyPage (or similar logic needed)
const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river'];
// Removed POSITION_ORDER_SIMPLE as it wasn't used here
// Removed getOrderedPositions as it wasn't used here
// End Helper

// Define the TimelineAction interface locally (Extends BaseAction from ./types)
interface TimelineAction extends BaseAction {
  isAllIn?: boolean;
}

// Helper to format action label
const getActionDisplayLabel = (action: TimelineAction): string => {
  switch (action.type) {
    case 'Bet': 
      return `Bet ${action.amount !== undefined ? action.amount + 'bb' : ''}`; // Append bb
    case 'Raise': 
      return `Raise ${action.amount !== undefined ? action.amount + 'bb' : ''}`; // Append bb
    case 'Call': 
      return 'Call'; // Display only "Call"
    default: 
      return action.type;
  }
};

// --- Define Styles Object at Module Level --- 
const styles = {
  trainPageContainer: {
      padding: '1rem',
      color: '#e5e7eb',
      backgroundColor: 'transparent',
      maxWidth: '1100px',
      margin: '0 auto'
  } as React.CSSProperties,
  explanationBox: {
      border: '1px dashed #374151',
      padding: '15px',
      marginBottom: '20px',
      backgroundColor: 'rgba(17,24,39,0.6)',
      color: '#d1d5db',
      borderRadius: '8px'
  } as React.CSSProperties,
  timelineContainer: {
      marginTop: '20px'
  } as React.CSSProperties,
  streetsTimeline: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(4, 1fr)',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid #1f2937',
    borderRadius: '10px',
    padding: '16px',
    overflowX: 'hidden',
    marginTop: '10px',
    color: '#f3f4f6'
  } as React.CSSProperties,
  streetHeader: {
    fontSize: '14px',
    fontWeight: 700,
    textAlign: 'center',
    padding: '8px',
    textTransform: 'uppercase',
    borderBottom: '1px solid #1f2937',
    color: '#e5e7eb'
  } as React.CSSProperties,
  potTotal: {
    display: 'block',
    marginTop: '4px',
    color: '#93c5fd',
    fontSize: '12px',
    fontWeight: 500,
    background: 'rgba(255, 255, 255, 0.06)',
    padding: '2px 6px',
    borderRadius: '4px'
  } as React.CSSProperties,
  playerLabel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 6px',
    color: '#e5e7eb',
    fontWeight: 700,
    fontSize: '0.9em',
    textAlign: 'right',
    borderRight: '1px solid #1f2937'
  } as React.CSSProperties,
  stackSize: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#9ca3af'
  } as React.CSSProperties,
  streetCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid #1f2937',
    borderRadius: '6px',
    minHeight: '60px',
    transition: 'all 0.2s'
  } as React.CSSProperties,
  timelineDots: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px 16px',
    alignItems: 'center',
    padding: '2px 0',
    position: 'relative',
    minHeight: '20px'
  } as React.CSSProperties,
  timelineDot: {
    position: 'relative',
    width: '14px',
    height: '14px',
    backgroundColor: '#9ca3af',
    borderRadius: '50%',
    margin: '2px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'default',
    transition: 'background-color 0.2s ease'
  } as React.CSSProperties,
  visibleActionLabel: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: '6px',
      backgroundColor: 'rgba(17,24,39,0.95)',
      color: '#f3f4f6',
      padding: '4px 8px',
      borderRadius: '6px',
      border: '1px solid #1f2937',
      boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      fontSize: '0.8rem',
      lineHeight: 1.2,
      whiteSpace: 'normal',
      maxWidth: '160px',
      zIndex: 10,
      visibility: 'visible',
      opacity: 1
  } as React.CSSProperties,
  // Inline style for Card component (revert to app default white card look)
  card: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '62px',
    backgroundColor: '#ffffff',
    border: '1px solid #cccccc',
    borderRadius: '6px',
    margin: '0 2px',
    padding: '2px',
    fontSize: '16px',
    fontWeight: 700,
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
  } as React.CSSProperties,
  cardRank: {
    fontSize: '1.05em'
  } as React.CSSProperties,
  cardSuit: {
    fontSize: '1em'
  } as React.CSSProperties,
  // Mobile-only helpers
  mobileStreetSection: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid #1f2937',
    borderRadius: '10px',
    padding: '10px',
    marginBottom: '12px'
  } as React.CSSProperties,
  mobileStreetTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#e5e7eb',
    textTransform: 'uppercase',
    marginBottom: '8px',
    borderBottom: '1px solid #1f2937',
    paddingBottom: '6px'
  } as React.CSSProperties,
  mobilePlayerRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '8px 0',
    borderBottom: '1px dashed #1f2937'
  } as React.CSSProperties,
  mobilePlayerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#e5e7eb',
    fontWeight: 600
  } as React.CSSProperties,
  mobileTimelineDots: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px 10px',
    alignItems: 'center'
  } as React.CSSProperties,
  mobileTabsContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  } as React.CSSProperties,
  mobileTab: {
    flex: 1,
    background: '#000',
    color: '#bbb',
    border: '1px solid #222',
    borderRadius: '8px',
    padding: '8px 10px'
  } as React.CSSProperties,
  mobileTabActive: {
    color: '#fff',
    borderColor: '#444',
    background: '#0a0a0a'
  } as React.CSSProperties,
};
// --- End Styles Object Definition ---

// --- Card Component (Defined Inline) --- 
const Card: React.FC<{ card: string }> = ({ card }) => {
  if (!card || card.length < 2) {
    return <div style={{ ...styles.card, background: '#eee' }}>?</div>; // Placeholder for invalid card
  }
  const rank = card.substring(0, card.length - 1);
  const suit = card.substring(card.length - 1);
  const suitSymbol = { s: '♠', h: '♥', d: '♦', c: '♣' }[suit];
  const color = (suit === 'h' || suit === 'd') ? 'red' : 'black';

  return (
    <span style={{ ...styles.card, color: color }}>
      <span style={styles.cardRank}>{rank}</span>
      <span style={styles.cardSuit}>{suitSymbol}</span>
    </span>
  );
};
// --- End Card Component --- 

const TrainPage: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string | null>(null);
  
  const [isLoadingSituations, setIsLoadingSituations] = useState(false);
  const [fetchMessage, setFetchMessage] = useState<string | null>(null);
  const [situations, setSituations] = useState<Situation[]>([]);
  const [currentSituationIndex, setCurrentSituationIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [activeMobileStreet, setActiveMobileStreet] = useState<Street>('preflop');

  // --- ADDED: State for polling ---
  const [isPollingSituations, setIsPollingSituations] = useState(false);
  const [lastSituationTimestamp, setLastSituationTimestamp] = useState<string | null>(null);
  const pollingIntervalId = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTime = useRef<number | null>(null);
  const POLLING_INTERVAL_MS = 3000; // Check every 3 seconds
  const POLLING_TIMEOUT_MS = 120000; // Stop polling after 2 minutes
  // --- END ADDED State ---

  // --- Generation Logic --- 
  const handleGenerateSituations = async () => {
    setIsGenerating(true);
    // Reset fetch message when starting generation
    setFetchMessage(null);
    setGenerationMessage('Triggering situation generation...'); 
    try {
      if (!PlayFab || !PlayFab.executeCloudScript) throw new Error("PlayFab service not available.");
      
      const result = await PlayFab.executeCloudScript({
        FunctionName: 'triggerSituationGeneration',
        FunctionParameter: {}
      });

      if (result.success && result.data?.FunctionResult?.FunctionResult) {
          const functionResult = result.data.FunctionResult.FunctionResult;
          if (functionResult.success) {
              // --- MODIFIED: Update message and start polling ---
              setGenerationMessage(`Success: ${functionResult.message}`);
              console.log("Generation triggered, starting polling...");
              startPollingForSituations(); 
              // --- END MODIFICATION ---
          } else {
              const errorMsg = functionResult.error || 'Failed to trigger generation.';
              setGenerationMessage(`Error: ${errorMsg}`);
              console.error("CloudScript function returned an error:", errorMsg, functionResult);
          }
      } else {
        const errorMsg = 'API call failed to execute CloudScript. See console for details.';
        setGenerationMessage(`Error: ${errorMsg}`);
        console.error("API Call Error or Malformed Response executing CloudScript:", result);
      }
    } catch (error: any) {
      console.error("Error calling triggerSituationGeneration:", error);
      setGenerationMessage(`Error: Failed to communicate with server. ${error.message || ''}`);
    } finally {
      // Don't set isGenerating false here, let polling handle status message
      // setIsGenerating(false); 
    }
  };

  // --- Fetching Logic (Modified for initial timestamp setting) --- 
  const fetchSituations = async () => {
    setIsLoadingSituations(true);
    setFetchMessage('Fetching training situations...');
    setSituations([]); // Clear previous situations
    setSelectedAnswer(null);
    setShowExplanation(false);
    setCurrentSituationIndex(0);
    // Stop polling if manually fetching
    stopPolling(); 

    try {
      if (!PlayFab || !PlayFab.executeCloudScript) throw new Error("PlayFab service not available.");

      const result = await PlayFab.executeCloudScript({
        FunctionName: 'getMyTrainingSituations',
        FunctionParameter: {}
      });

      if (result.success && result.data?.FunctionResult?.FunctionResult) {
          const functionResult = result.data.FunctionResult.FunctionResult;
          if (functionResult.success && Array.isArray(functionResult.situations)) {
              if (functionResult.situations.length > 0) {
                  setSituations(functionResult.situations);
                  
                  // --- ADDED: Find and set latest timestamp after fetch ---
                  let maxTimestamp: string | null = null;
                  functionResult.situations.forEach((sit: Situation) => {
                      if (sit.generationTimestamp) {
                          if (maxTimestamp === null || new Date(sit.generationTimestamp) > new Date(maxTimestamp)) {
                              maxTimestamp = sit.generationTimestamp;
                          }
                      }
                  });
                  if (maxTimestamp) {
                      setLastSituationTimestamp(maxTimestamp);
                      console.log("Set lastSituationTimestamp after fetch:", maxTimestamp);
                  } else {
                      setLastSituationTimestamp(null);
                  }
                  // --- END ADDED ---
                  
                  setFetchMessage(`Successfully fetched ${functionResult.situations.length} situations.`);
              } else {
                  setFetchMessage('No training situations found. Try generating some!');
                  setLastSituationTimestamp(null); // Reset timestamp if none found
              }
          } else {
              const errorMsg = functionResult.error || 'Failed to retrieve situations.';
              setFetchMessage(`Error: ${errorMsg}`);
              console.error("CloudScript Error:", functionResult);
              setLastSituationTimestamp(null); // Reset timestamp on error
          }
      } else {
        const errorMsg = 'API call failed. See console for details.';
        setFetchMessage(`Error: ${errorMsg}`);
        console.error("API Call Error (getMyTrainingSituations):", result);
        setLastSituationTimestamp(null); // Reset timestamp on error
      }
    } catch (error: any) {
      console.error("Error calling getMyTrainingSituations:", error);
      setFetchMessage(`Error: Failed to communicate with server. ${error.message || ''}`);
      setLastSituationTimestamp(null); // Reset timestamp on error
    } finally {
      setIsLoadingSituations(false);
    }
  };

  // --- ADDED: Polling Functions --- 
  const pollCheck = async () => {
    console.log("Polling check...");
    if (!PlayFab || !PlayFab.executeCloudScript) {
        console.error("Polling check failed: PlayFab service not available.");
        stopPolling(); // Stop if PlayFab is gone
        setGenerationMessage("Error: Connection lost during check.");
        return;
    }
    
    // Check for timeout
    if (pollingStartTime.current && (Date.now() - pollingStartTime.current > POLLING_TIMEOUT_MS)) {
        console.warn("Polling timed out.");
        setGenerationMessage("Checking for results timed out. Please refresh manually if needed.");
        stopPolling();
        return;
    }

    try {
      const result = await PlayFab.executeCloudScript({
        FunctionName: 'getMyTrainingSituations',
        FunctionParameter: {}
      });

      if (result.success && result.data?.FunctionResult?.FunctionResult) {
          const functionResult = result.data.FunctionResult.FunctionResult;
          if (functionResult.success && Array.isArray(functionResult.situations) && functionResult.situations.length > 0) {
                
                // Find the latest timestamp among fetched situations
                let maxTimestamp: string | null = null;
                functionResult.situations.forEach((sit: Situation) => {
                    if (sit.generationTimestamp) {
                        if (maxTimestamp === null || new Date(sit.generationTimestamp) > new Date(maxTimestamp)) {
                            maxTimestamp = sit.generationTimestamp;
                        }
                    }
                });
                
                // --- ADDED: Log timestamps before comparison ---
                console.log(`[pollCheck] Comparing Timestamps - Last Known: ${lastSituationTimestamp}, Max Fetched: ${maxTimestamp}`);
                // --- END ADDED ---
                
                // Check if the latest fetched timestamp is newer than the last known one
                if (maxTimestamp && (lastSituationTimestamp === null || new Date(maxTimestamp) > new Date(lastSituationTimestamp))) {
                    console.log(`New situation found (Timestamp: ${maxTimestamp}). Stopping poll and refreshing.`);
                    stopPolling();
                    setGenerationMessage(null); // Clear generation message
                    fetchSituations(); // Call the main fetch function to update UI
                } else {
                    console.log("No new situations found yet.");
                    // Update message to show polling is active
                    setGenerationMessage("Generation triggered. Checking for results..."); 
                }
          } else {
              // No situations found or CloudScript error
              console.warn("Polling check: No situations found or CloudScript error.", functionResult?.error);
              // Keep polling, but maybe log the error if present
          }
      } else {
        // API call error
        console.error("Polling check: API call failed.", result);
        // Possibly stop polling on persistent API errors? For now, keep polling.
      }
    } catch (error: any) {
      console.error("Error during pollCheck:", error);
      // Stop polling on network errors etc.
      setGenerationMessage("Error checking for results. Please refresh manually.");
      stopPolling(); 
    }
  };

  const startPollingForSituations = () => {
    stopPolling(); // Clear any existing interval first
    setIsGenerating(false); // Generation is done, now polling
    setIsPollingSituations(true);
    setGenerationMessage("Generation triggered. Checking for results..."); // Initial polling message
    pollingStartTime.current = Date.now(); // Record start time for timeout
    pollingIntervalId.current = setInterval(pollCheck, POLLING_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollingIntervalId.current) {
      clearInterval(pollingIntervalId.current);
      pollingIntervalId.current = null;
      console.log("Polling stopped.");
    }
    setIsPollingSituations(false);
    pollingStartTime.current = null;
    // Only clear generation message if polling is manually stopped or succeeds/fails
    // Don't clear it if fetchSituations is called immediately after stopping
  };
  // --- END ADDED: Polling Functions ---

  // Fetch situations when component mounts
  useEffect(() => {
    fetchSituations();
    // Cleanup function to stop polling when component unmounts
    return () => {
        stopPolling();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Mobile detection
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Reset active street when situation changes
  useEffect(() => {
    setActiveMobileStreet('preflop');
  }, [currentSituationIndex]);

  // --- Interaction Logic --- 
  const handleSelectOption = (action: string) => {
    if (showExplanation) return; // Don't allow changing answer after revealing
    setSelectedAnswer(action);
    setShowExplanation(true); // Show explanation immediately after selection
  };

  const handleNext = () => {
    if (currentSituationIndex < situations.length - 1) {
      setCurrentSituationIndex(currentSituationIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      // Optionally fetch new ones if at the end?
      // fetchSituations(); 
    }
  };

  const handlePrevious = () => {
    if (currentSituationIndex > 0) {
      setCurrentSituationIndex(currentSituationIndex - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const currentSituation = situations[currentSituationIndex];

  // Helper function to get the full board cards
  const getBoardCards = (): string[] => {
    if (!currentSituation) return [];
    const board: string[] = [];
    if (currentSituation.streets.flop?.board) {
      board.push(...currentSituation.streets.flop.board);
    }
    if (currentSituation.streets.turn?.card) {
      board.push(currentSituation.streets.turn.card);
    }
    if (currentSituation.streets.river?.card) {
      board.push(currentSituation.streets.river.card);
    }
    return board;
  };

  const boardCards = getBoardCards();

  // --- Function to adapt Situation data to Player[] format --- 
  const adaptSituationToPlayers = (situation: Situation | null): Player[] => {
    if (!situation) return [];
    
    const tempPlayers: { [position: string]: Player } = {};
    const playerNames: { [key: string]: string } = {};
    const playerStacks: { [key: string]: number } = {};

    if (situation.positions) {
        playerNames[situation.positions.hero] = 'Hero';
        playerNames[situation.positions.villain] = 'Villain';
    }
     if (situation.stacks?.effective) { 
         if(situation.positions) {
             playerStacks[situation.positions.hero] = situation.stacks.effective * 100; 
             playerStacks[situation.positions.villain] = situation.stacks.effective * 100;
         }
     }

    STREET_ORDER.forEach(street => {
        const streetData = situation.streets[street as keyof Situation['streets']];
        if (streetData && (streetData as any).actions && (streetData as any).actions.length > 0) { 
            (streetData as any).actions.forEach((action: StreetAction) => {
                // --- Robust Position Handling ---
                let position = action.player as Position;
                // Try to map common variations if needed (example)
                if (position.toLowerCase() === 'button') position = 'BTN';
                // Add more mappings if necessary 
                
                // Validate if the position is known in your Position type
                const knownPositions: Position[] = ['SB', 'BB', 'UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN']; 
                if (!knownPositions.includes(position)) {
                    console.warn(`Unknown player position "${action.player}" found in situation ${situation.id}, street ${street}. Skipping action.`);
                    return; // Skip this action if position is invalid
                }
                // --- End Robust Position Handling ---
                
                if (!tempPlayers[position]) {
                    tempPlayers[position] = {
                        id: Object.keys(tempPlayers).length,
                        name: playerNames[position] || position,
                        position: position,
                        stack: playerStacks[position] || 10000, // Default high stack
                        isHero: playerNames[position] === 'Hero',
                        actions: { preflop: [], flop: [], turn: [], river: [] }
                    };
                }
                
                 let amount: number | undefined = undefined;
                 let isAllIn: boolean | undefined = undefined;
                 const actionString = action.action.toLowerCase(); // Use lowercase for easier matching
                 
                 // Determine ActionType more robustly
                 let actionType: ActionType | null = null; // Initialize as null
                 if (actionString.startsWith('bet')) actionType = 'Bet';
                 else if (actionString.startsWith('raise')) actionType = 'Raise';
                 else if (actionString.startsWith('call')) actionType = 'Call';
                 else if (actionString.startsWith('check')) actionType = 'Check';
                 else if (actionString.startsWith('fold')) actionType = 'Fold';

                 // If no valid type determined, skip this action
                 if (actionType === null) {
                     console.warn(`Could not determine valid ActionType for: "${action.action}". Skipping.`);
                     return; // Use return to skip this iteration of forEach
                 }

                 // --- Revised Amount Parsing --- 
                 if (actionType === 'Bet' || actionType === 'Raise' || actionType === 'Call') {
                      // Regex to capture numbers (int/float) optionally followed by 'bb'
                      const amountMatch = actionString.match(/([0-9]+(?:\.[0-9]+)?)\s*(?:bb)?/);
                      if (amountMatch && amountMatch[1]) {
                          const num = parseFloat(amountMatch[1]);
                          if (!isNaN(num)) {
                              amount = num;
                          }
                      } else if (actionType === 'Call') {
                          // Special case for 'calls' with no amount - find previous bet/raise
                          // This requires looking at previous actions *in sequence* which is complex here.
                          // For simplicity, we might assume call amount needs explicit parsing or context.
                          console.warn(`Could not parse amount for simple 'Call' action: ${actionString}. Amount needed.`);
                      }
                  }
                 // --- End Revised Amount Parsing --- 

                  if (actionString.includes('all-in')) {
                      isAllIn = true;
                  }

                // actionType is now guaranteed to be a valid ActionType due to the check above
                const baseAction: TimelineAction = {
                    type: actionType, 
                    ...(amount !== undefined && { amount: amount }),
                    ...(isAllIn !== undefined && { isAllIn: isAllIn }),
                };
                
                if (!tempPlayers[position].actions[street]) {
                  tempPlayers[position].actions[street] = [];
                }
                tempPlayers[position].actions[street]!.push(baseAction);
            });
        }
    });
    return Object.values(tempPlayers);
  };

  // Get the adapted player data for the current situation
  const timelinePlayers = adaptSituationToPlayers(currentSituation);

  // Quick UI-only helper: split explanation into bullet points by paragraphs or sentences
  const getExplanationBullets = (text?: string): string[] => {
    if (!text) return [];
    // Prefer paragraph split first
    const paragraphSplit = text
      .split(/\n+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (paragraphSplit.length > 1) return paragraphSplit;
    // Otherwise split by sentences (., !, ? followed by space or end)
    const sentenceSplit = text
      .replace(/\s+/g, ' ')
      .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
      .map(s => s.trim())
      .filter(Boolean);
    return sentenceSplit.length ? sentenceSplit : [text.trim()];
  };

  // Decide what message to show in the generation area
  let displayMessage = generationMessage || fetchMessage;
  if (isPollingSituations && !generationMessage?.startsWith('Error')) {
      displayMessage = "Checking for new situations...";
  }
  if (isLoadingSituations) {
      displayMessage = "Loading situations...";
  }

  return (
    <div style={styles.trainPageContainer}>
      <h1 style={{ color: '#f3f4f6', margin: '0 0 8px 0' }}>Poker Training Situations</h1>
      <p style={{ color: '#9ca3af', marginTop: 0 }}>Practice spots tailored to your play. New results appear automatically.</p>

      {/* --- Generation Section --- */}
      <div style={{ margin: '16px 0 20px 0', border: '1px solid #222', padding: '16px', borderRadius: '10px', background: '#000' }}>
        <h2 style={{ marginTop: 0, color: '#e5e7eb' }}>Generate Situations (Premium)</h2>
        <p style={{ color: '#d1d5db' }}>Click the button below to generate a new batch of personalized training situations (requires premium). Results will appear automatically when ready.</p>
        <button 
            onClick={handleGenerateSituations} 
            disabled={isGenerating || isPollingSituations || isLoadingSituations}
        >
          {isGenerating ? 'Triggering...' : (isPollingSituations ? 'Checking...' : 'Generate New Training Situations')}
        </button>
        {/* Display unified message */}
        {displayMessage && (
          <p style={{ marginTop: '10px', fontWeight: displayMessage.includes('Error') ? 700 : 500, color: displayMessage.includes('Error') ? '#f87171' : (isPollingSituations ? '#60a5fa' : '#34d399') }}>
            {displayMessage}
          </p>
        )}
      </div>

      {currentSituation ? (
        <div style={{ border: '1px solid #222', padding: '16px', marginTop: '12px', borderRadius: '10px', background: '#000' }}>
          <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Situation {currentSituationIndex + 1} of {situations.length}</h3>
          <p style={{ color: '#d1d5db' }}><strong>Difficulty:</strong> {currentSituation.difficulty}</p>
          
          {/* --- Display Hand and Board --- */}
          <div style={{ margin: '15px 0' }}>
            <strong>Board:</strong>
            <div style={{ marginTop: '5px' }}>
              {boardCards.length > 0 
                ? boardCards.map((card, index) => <Card key={`board-${index}`} card={card} />)
                : <span style={{ fontStyle: 'italic' }}> No board cards yet</span>
              }
            </div>
          </div>
          <div style={{ margin: '15px 0' }}>
            <strong>Hero Hand ({currentSituation.positions?.hero || 'N/A'}):</strong>
            <div style={{ marginTop: '5px' }}>
              {currentSituation.streets.preflop.hero_hand.map((card, index) => (
                <Card key={`hero-${index}`} card={card} />
              ))}
            </div>
          </div>
          {/* --- End Display Hand and Board --- */}
          
          {/* --- RENDER INLINE-STYLED TIMELINE GRID --- */}
          <div style={styles.timelineContainer}>
            <h3 style={{color: '#d1d5db'}}>Action History</h3>
            {!isMobile ? (
              <div style={styles.streetsTimeline}>
                {/* Street Headers */}
                {STREET_ORDER.map(street => (
                  <div key={street} style={styles.streetHeader}>
                    <span>{street.charAt(0).toUpperCase() + street.slice(1)}</span>
                  </div>
                ))}

                {/* Player Rows */}
                {timelinePlayers.map(player => (
                  <React.Fragment key={player.id}>
                    <div style={styles.playerLabel}>
                      <div>{player.name} ({player.position})</div>
                      <div style={styles.stackSize}>Stack: {player.stack}</div>
                    </div>
                    {STREET_ORDER.map(street => {
                       const streetActions = player.actions?.[street] || [];
                      return (
                        <div key={street} style={styles.streetCell}>
                          <div style={styles.timelineDots}>
                            {streetActions.map((action, index) => (
                              <div key={index} style={styles.timelineDot}>
                                 <span style={styles.visibleActionLabel}>
                                  {getActionDisplayLabel(action as TimelineAction)} 
                                  {(action as TimelineAction).isAllIn && ' (All-in)'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              // Mobile layout: streets in tabs using Radix Tabs to match HandNotesPage
              <Tabs.Root value={activeMobileStreet} onValueChange={(v)=>setActiveMobileStreet(v as Street)} className="tabs-root">
                <Tabs.List className="tabs-list">
                  <Tabs.Trigger value="preflop" className="tabs-trigger">Preflop</Tabs.Trigger>
                  <Tabs.Trigger value="flop" className="tabs-trigger">Flop</Tabs.Trigger>
                  <Tabs.Trigger value="turn" className="tabs-trigger">Turn</Tabs.Trigger>
                  <Tabs.Trigger value="river" className="tabs-trigger">River</Tabs.Trigger>
                </Tabs.List>
                {STREET_ORDER.map((street) => (
                  <Tabs.Content key={`content-${street}`} value={street} className="tabs-content">
                    <div style={styles.mobileStreetSection}>
                      {timelinePlayers.map((player) => {
                        const streetActions = player.actions?.[street] || [];
                        return (
                          <div key={`m-${street}-${player.id}`} style={styles.mobilePlayerRow}>
                            <div style={styles.mobilePlayerHeader}>
                              <span>{player.name} ({player.position})</span>
                              <span style={{ color: '#9ca3af', fontWeight: 500 }}>Stack: {player.stack}</span>
                            </div>
                            <div style={styles.mobileTimelineDots}>
                              {streetActions.length === 0 && (
                                <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>No actions</span>
                              )}
                              {streetActions.map((action, index) => (
                                <div key={`m-${street}-${player.id}-${index}`} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                  <div style={styles.timelineDot} />
                                  <span style={{ ...styles.visibleActionLabel, position: 'static', transform: 'none', marginLeft: 6 }}>
                                    {getActionDisplayLabel(action as TimelineAction)}
                                    {(action as TimelineAction).isAllIn && ' (All-in)'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Tabs.Content>
                ))}
              </Tabs.Root>
            )}
          </div>
          {/* --- END INLINE-STYLED TIMELINE GRID --- */}
          
          <p style={{ marginTop: '15px', marginBottom: '15px', fontSize: '1.1em', whiteSpace: 'pre-wrap' }}><strong>Question:</strong> {currentSituation.question}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {currentSituation.options.map((option, index) => {
              const isCorrect = option.is_correct;
              const isSelected = selectedAnswer === option.action;
              let buttonStyle: React.CSSProperties = { padding: '10px', textAlign: 'left' };

              if (showExplanation) {
                if (isSelected) {
                  buttonStyle.backgroundColor = isCorrect ? '#065f46' : '#7f1d1d';
                  buttonStyle.border = `1px solid ${isCorrect ? '#10b981' : '#ef4444'}`;
                } else if (isCorrect) {
                  buttonStyle.backgroundColor = '#064e3b';
                  buttonStyle.border = '1px solid #10b981';
                }
                // Ensure text remains readable even when disabled
                buttonStyle.color = '#ffffff';
                buttonStyle.cursor = 'default';
              } else {
                 buttonStyle.cursor = 'pointer';
              }

              return (
                <button 
                  key={index} 
                  onClick={() => handleSelectOption(option.action)}
                  disabled={showExplanation}
                  style={buttonStyle}
                >
                  {option.action}
                  {showExplanation && isSelected && (isCorrect ? ' (Correct)' : ' (Incorrect)')}
                  {showExplanation && !isSelected && isCorrect && ' (Correct Answer)'}
                </button>
              );
            })}
          </div>

          {showExplanation && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#000', border: '1px dashed #222', borderRadius: '8px' }}>
              <h4 style={{color: '#e5e7eb'}}>Explanation:</h4>
              <ul style={{ margin: '8px 0 0 18px', padding: 0, color: '#d1d5db', listStyleType: 'disc' }}>
                {getExplanationBullets(currentSituation.explanation).map((point, idx) => (
                  <li key={`exp-${idx}`} style={{ marginBottom: '6px' }}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Navigation */} 
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={handlePrevious} disabled={currentSituationIndex === 0 || isLoadingSituations || isPollingSituations}>
              Previous
            </button>
            <button onClick={handleNext} disabled={currentSituationIndex === situations.length - 1 || isLoadingSituations || isPollingSituations}>
              Next
            </button>
          </div>
        </div>
      ) : (
         // Show message only if not loading and no error fetching, and not currently polling
         !isLoadingSituations && !fetchMessage?.startsWith('Error') && !isPollingSituations && <p>No situations loaded. Try generating some.</p>
      )}

    </div>
  );
};

export default TrainPage; 